import React, { useEffect, useMemo, useRef, useState } from "react";

import type { PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";

import { AlertV4, ButtonV4, FieldV4, ModalV4, PaginationV4 } from "../common/ui";

import type { RegistrationV4SequenceState } from "../common/commonV4Interface";
import { ACTIONS } from "../common/sequence/RegistrationV4";
import {
  formatGroupRows,
  formatGroupRowsFromSheet,
  type GroupRow,
  type PatientListItem,
} from "./registrationModelV4";

import "./_hisv4-registration.scss";

export type OpenPatientTarget = "pos" | "encounter" | "info" | "history";

type SetPatientSlice = Partial<{
  sequenceIndex: string | null;
  patientList: PatientListItem[] | null;
  patientListTotal: number;
  page: number;
  patientEmergency: { first_name?: string; last_name?: string; gender?: string } | null;
  patientEmergencyList: Partial<GroupRow>[] | null;
}> | null;

export type CardSearchMemberV4Props = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "successMessage"
> & {
  setProp: (key: string, value: unknown) => void;
  runSequence: (params: Record<string, unknown>) => void;
  RegistrationV4Sequence?: RegistrationV4SequenceState;
  SetPatientSequence?: SetPatientSlice;
  onOpenPatient: (target: OpenPatientTarget, patientId?: number) => void;
};

const HEADERS = [
  "HN",
  "ชื่อ",
  "วันเดือนปีเกิด",
  "เลขบัตรประชาชน/พาสปอร์ต",
  "ชื่อเดิม",
  "ชื่อมารดา",
  "ที่อยู่ปัจจุบัน",
  "เบอร์โทรศัพท์",
];

const SEARCH_CARD = "CardSearchMemberV4";

const EMERGENCY_CARD = "ModEmergencyV4";

const SEARCH_LIMIT = 20;

// key ใน buttonLoadCheck / errorMessage ที่ SetPatient ใช้ (`<card>_<action>`)
const SEARCH_KEY = `${SEARCH_CARD}_search`;

const SMART_CARD_KEY = `${SEARCH_CARD}_smartCard`;

const GROUP_KEY = `${SEARCH_CARD}_register_emergency`;

const EMERGENCY_GENDERS = [
  { value: "M", label: "ชาย" },
  { value: "F", label: "หญิง" },
  { value: "CM", label: "เด็กชาย" },
  { value: "CF", label: "เด็กหญิง" },
] as const;

const dash = (text?: string) => text || "-";

const CardSearchMemberV4 = (props: CardSearchMemberV4Props) => {
  const [searchText, setSearchText] = useState("");
  const [citizenText, setCitizenText] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [requested, setRequested] = useState(false);
  const [openEmergency, setOpenEmergency] = useState(false);
  const [openGroup, setOpenGroup] = useState(false);
  const [groupFile, setGroupFile] = useState<GroupRow[]>([]);

  const fileRef = useRef<HTMLInputElement>(null);

  const seq = props.SetPatientSequence;
  const rows = seq?.patientList ?? [];

  const total = seq?.patientListTotal ?? 0;
  const page = seq?.page ?? 1;
  const pageCount = Math.max(1, Math.ceil(total / SEARCH_LIMIT));

  const selected = rows.find((row) => row.id === selectedId);

  // หลัง select sequence อยู่ขั้น EditPatientInfo: เริ่มใหม่ให้กลับมาขั้นค้นหา
  const restart = seq?.sequenceIndex !== "SearchOrNew";

  const searching =
    props.buttonLoadCheck?.[SEARCH_KEY] === "LOADING" || (requested && !seq?.patientList);

  const smartCardError = props.errorMessage?.[SMART_CARD_KEY]?.error;

  const emptyText = searching
    ? "กำลังค้นหาผู้ป่วย..."
    : seq?.patientList
      ? 'ไม่พบผู้ป่วยที่ตรงกับคำค้นหา · กด "+ เปิดประวัติ" เพื่อสร้างประวัติใหม่'
      : "กรุณาทำการค้นหาผู้ป่วย";

  const groupRows = useMemo(
    () =>
      seq?.patientEmergencyList?.length ? formatGroupRows(seq.patientEmergencyList) : groupFile,
    [seq?.patientEmergencyList, groupFile],
  );

  // เข้าหน้าครั้งแรก: ค้นหาทั้งหมด
  useEffect(() => {
    if (!seq?.patientList) {
      handleSearch();
    }
  }, []);

  // ระดับสมาชิกของแถวที่ค้นเจอ
  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    props.runSequence({
      sequence: "RegistrationV4",
      action: ACTIONS.LOAD_TIERS,
      patientIds: rows.map((row) => row.id),
    });
  }, [seq?.patientList]);

  useEffect(() => {
    if (props.successMessage?.[EMERGENCY_CARD]) {
      setOpenEmergency(false);
      props.setProp(`successMessage.${EMERGENCY_CARD}`, null);
    }
  }, [props.successMessage?.[EMERGENCY_CARD]]);

  const handleSearch = (nextPage = 1) => {
    setRequested(true);

    props.runSequence({
      sequence: "SetPatient",
      action: "search",
      searchText,
      citizenText,
      page: nextPage,
      limit: SEARCH_LIMIT,
      card: SEARCH_CARD,
      restart,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleNew = (patient: Record<string, unknown> = {}) => {
    props.runSequence({ sequence: "SetPatient", action: "new", patient, restart, registrationV4: true });

    props.onOpenPatient("info");
  };

  const handleOpen = (target: OpenPatientTarget) => {
    if (!selected) {
      return;
    }

    props.runSequence({
      sequence: "SetPatient",
      action: "select",
      patient: { id: selected.id },
      restart,
    });

    props.onOpenPatient(target, selected.id);
  };

  const handleSmartCard = () => {
    props.runSequence({ sequence: "SetPatient", action: "smartCard", card: SEARCH_CARD, restart });
  };

  const handleSmartCardNew = () => {
    const data = props.errorMessage?.[SMART_CARD_KEY]?.data ?? {};

    props.setProp(`errorMessage.${SMART_CARD_KEY}`, null);

    handleNew(data);
  };

  const handleCloseSmartCardError = () => {
    props.setProp(`errorMessage.${SMART_CARD_KEY}`, null);
  };

  const handleClearEmergency = () => {
    props.runSequence({
      sequence: "SetPatient",
      action: "clear_emergency",
      card: EMERGENCY_CARD,
      restart,
    });
  };

  const handleOpenEmergency = () => {
    handleClearEmergency();

    props.setProp(`errorMessage.${EMERGENCY_CARD}`, null);

    setOpenEmergency(true);
  };

  const handleChangeEmergency = (key: string, value: string) => {
    props.setProp(`SetPatientSequence.patientEmergency.${key}`, value);
  };

  const handleSaveEmergency = () => {
    props.runSequence({
      sequence: "SetPatient",
      action: "patient_emergency",
      card: EMERGENCY_CARD,
      forward: () => props.onOpenPatient("encounter"),
      restart,
    });
  };

  const handleCloseGroup = () => {
    setOpenGroup(false);
    setGroupFile([]);

    props.setProp("SetPatientSequence.patientEmergencyList", []);
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(event.target?.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];

      props.setProp("SetPatientSequence.patientEmergencyList", []);

      setGroupFile(formatGroupRowsFromSheet(XLSX.utils.sheet_to_json(sheet)));
    };

    reader.readAsBinaryString(file);

    e.target.value = "";
  };

  const handleRegisterGroup = () => {
    props.runSequence({
      sequence: "SetPatient",
      action: "register_emergency",
      patient: groupFile,
      buttonLoadKey: GROUP_KEY,
      restart,
    });
  };

  const renderTier = (row: PatientListItem) => {
    const tier = props.RegistrationV4Sequence?.tiers?.[row.id];
    if (tier === undefined) {
      return null;
    }
    if (tier === null)
      return <span className="hv4-reg-tier hv4-reg-tier-none">ยังไม่เป็นสมาชิก</span>;
    return <span className={`hv4-reg-tier hv4-reg-tier-${tier.tierIndex}`}>🏅 {tier.tier}</span>;
  };

  return (
    <div className="hv4-reg-search">
      {/* หัวหน้า */}
      <div className="hv4-page-head">
        <h1 className="hv4-h1">ค้นหาผู้ป่วย</h1>
        <span className="hv4-page-sub">
          ค้นหารายชื่อ หรือเปิดประวัติใหม่ แล้วเปิด Encounter ต่อได้จากหน้านี้
        </span>
      </div>

      {/* ช่องค้นหา + ปุ่ม */}
      <div className="hv4-reg-form">
        <FieldV4 label="HN / ชื่อ / นามสกุล" htmlFor="hv4-reg-q" className="hv4-reg-form-q">
          <input
            id="hv4-reg-q"
            className="hv4-input"
            placeholder="ค้นหาจาก HN, ชื่อ, นามสกุล"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </FieldV4>
        <FieldV4
          label="เลขบัตรประชาชน / พาสปอร์ต"
          htmlFor="hv4-reg-cid"
          className="hv4-reg-form-cid"
        >
          <input
            id="hv4-reg-cid"
            className="hv4-input"
            placeholder="ค้นหาจาก หมายเลขบัตรประชาชน"
            value={citizenText}
            onChange={(e) => setCitizenText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </FieldV4>
        <ButtonV4 variant="primary" disabled={searching} onClick={() => handleSearch()}>
          ค้นหา
        </ButtonV4>
        <ButtonV4 variant="ok" onClick={() => handleNew()}>
          + เปิดประวัติ
        </ButtonV4>
        <ButtonV4 variant="ok" onClick={() => setOpenGroup(true)}>
          + เปิดประวัติหมู่
        </ButtonV4>
        <ButtonV4
          className="hv4-reg-btn-smart"
          disabled={props.buttonLoadCheck?.[SMART_CARD_KEY] === "LOADING"}
          onClick={handleSmartCard}
        >
          Smart Card
        </ButtonV4>
        <ButtonV4 className="hv4-reg-btn-emergency" onClick={handleOpenEmergency}>
          เปิดประวัติฉุกเฉิน
        </ButtonV4>
      </div>

      <AlertV4
        tone="danger"
        error={smartCardError !== "NOT_FOUND" && smartCardError}
        onClose={handleCloseSmartCardError}
      />

      {/* ตาราง */}
      <div className="hv4-grid-table">
        <div className="hv4-reg-table-inner" role="table" aria-label="ผลการค้นหาผู้ป่วย">
          <div className="hv4-grid-row hv4-grid-row--head hv4-reg-row" role="row">
            {HEADERS.map((header) => (
              <div key={header} role="columnheader">
                {header}
              </div>
            ))}
          </div>
          {rows.length === 0 ? (
            <div className="hv4-grid-empty">{emptyText}</div>
          ) : (
            rows.map((row) => (
              <button
                key={row.id}
                type="button"
                role="row"
                aria-selected={row.id === selectedId}
                className={`hv4-grid-row hv4-reg-row${row.id === selectedId ? " hv4-grid-row--selected" : ""}`}
                onClick={() => setSelectedId(row.id)}
                onDoubleClick={() => {
                  setSelectedId(row.id);
                  props.runSequence({
                    sequence: "SetPatient",
                    action: "select",
                    patient: { id: row.id },
                    restart,
                  });
                  props.onOpenPatient("pos");
                }}
              >
                <span className="hv4-reg-row-hn" role="cell">
                  {dash(row.hn)}
                  {renderTier(row)}
                </span>
                <span className="hv4-reg-row-name" role="cell">
                  {dash(row.full_name)}
                </span>
                <span role="cell">{dash(row.birthdate)}</span>
                <span role="cell">{dash(row.citizen_passport)}</span>
                <span role="cell">{dash(row.old_name)}</span>
                <span role="cell">{dash(row.mother)}</span>
                <span role="cell">{dash(row.address)}</span>
                <span role="cell">
                  {dash(row.phone_num || row.present_mobile_no || row.tel_num)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* แถบล่าง */}
      <div className="hv4-reg-bar">
        <span className="hv4-reg-bar-text">
          {selected
            ? `เลือกแล้ว: ${selected.hn} · ${selected.full_name}`
            : "เลือกผู้ป่วยจากรายการ เพื่อเปิด Encounter"}
        </span>
        {selected && (
          <ButtonV4 variant="ok" onClick={() => handleOpen("pos")}>
            เลือกซื้อสินค้าและบริการ →
          </ButtonV4>
        )}
        <ButtonV4 disabled={!selected} onClick={() => handleOpen("history")}>
          ดูประวัติการรักษา
        </ButtonV4>
        <ButtonV4
          className="hv4-reg-btn-encounter"
          disabled={!selected}
          onClick={() => handleOpen("encounter")}
        >
          {selected ? "เปิด Encounter →" : "เปิด Encounter"}
        </ButtonV4>
      </div>

      <PaginationV4
        page={page}
        pageCount={pageCount}
        total={total}
        pageSize={SEARCH_LIMIT}
        disabled={searching}
        onChange={handleSearch}
      />

      {/* Smart Card: ไม่พบประวัติ */}
      {smartCardError === "NOT_FOUND" && (
        <ModalV4 title="ไม่พบรายการผู้ป่วย" width={480} onClose={handleCloseSmartCardError}>
          <div className="hv4-reg-modal-body">
            ไม่พบประวัติผู้ป่วยจากบัตรนี้ ต้องการเปิดประวัติใหม่ด้วยข้อมูลจากบัตรหรือไม่
          </div>
          <div className="hv4-reg-modal-foot">
            <ButtonV4 variant="ghost" onClick={handleCloseSmartCardError}>
              ปิด
            </ButtonV4>
            <ButtonV4 variant="ok" onClick={handleSmartCardNew}>
              เปิดประวัติใหม่
            </ButtonV4>
          </div>
        </ModalV4>
      )}

      {/* เปิดประวัติฉุกเฉิน */}
      {openEmergency && (
        <ModalV4 title="เปิดประวัติฉุกเฉิน" width={560} onClose={() => setOpenEmergency(false)}>
          <div className="hv4-reg-modal-body">
            <AlertV4 tone="danger" error={props.errorMessage?.[EMERGENCY_CARD]?.error} />
            <FieldV4 label="ชื่อ" htmlFor="hv4-reg-em-first">
              <input
                id="hv4-reg-em-first"
                className="hv4-input"
                value={seq?.patientEmergency?.first_name ?? ""}
                onChange={(e) => handleChangeEmergency("first_name", e.target.value)}
              />
            </FieldV4>
            <FieldV4 label="นามสกุล" htmlFor="hv4-reg-em-last">
              <input
                id="hv4-reg-em-last"
                className="hv4-input"
                value={seq?.patientEmergency?.last_name ?? ""}
                onChange={(e) => handleChangeEmergency("last_name", e.target.value)}
              />
            </FieldV4>
            <div role="radiogroup" aria-label="เพศ">
              <span className="hv4-label">เพศ *</span>
              <div className="hv4-reg-gender">
                {EMERGENCY_GENDERS.map((gender) => (
                  <label key={gender.value}>
                    <input
                      type="radio"
                      name="hv4-reg-em-gender"
                      value={gender.value}
                      checked={seq?.patientEmergency?.gender === gender.value}
                      onChange={() => handleChangeEmergency("gender", gender.value)}
                    />
                    {gender.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="hv4-reg-modal-foot">
            <ButtonV4 variant="ghost" onClick={handleClearEmergency}>
              ล้าง
            </ButtonV4>
            <ButtonV4 variant="ok" onClick={handleSaveEmergency}>
              บันทึก
            </ButtonV4>
          </div>
        </ModalV4>
      )}

      {/* เปิดประวัติหมู่ */}
      {openGroup && (
        <ModalV4 title="เปิดประวัติหมู่" width={760} onClose={handleCloseGroup}>
          <div className="hv4-reg-modal-body">
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={handleChangeFile} />
            <div className="hv4-reg-group">
              <table>
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>HN</th>
                    <th>คำนำหน้า</th>
                    <th>ชื่อ</th>
                    <th>สกุล</th>
                    <th>เพศ</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((row) => (
                    <tr key={row.no}>
                      <td>{row.no}</td>
                      <td>{row.hn}</td>
                      <td>{row.pre_name}</td>
                      <td>{row.first_name_th}</td>
                      <td>{row.last_name_th}</td>
                      <td>{row.gender}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="hv4-reg-modal-foot">
            <ButtonV4 variant="primary" onClick={() => fileRef.current?.click()}>
              อัพโหลดไฟล์
            </ButtonV4>
            <ButtonV4
              variant="ok"
              disabled={groupFile.length === 0 || props.buttonLoadCheck?.[GROUP_KEY] === "LOADING"}
              onClick={handleRegisterGroup}
            >
              ยืนยันเปิดประวัติ
            </ButtonV4>
          </div>
        </ModalV4>
      )}
    </div>
  );
};

export default CardSearchMemberV4;
